import React, { memo } from "react";
import { Card } from "../../common";
import { LucideIcon, CheckCircle, XCircle, FileDown } from "lucide-react";

//#region 型定義
interface ConversionResultProps {
  result: {
    success: boolean;
    message: string;
    convertedFilePath: string | null;
    errorDetails?: string;
    warnings?: string[];
    additionalInfo?: { [key: string]: string };
  };
}

type ResultStyle = {
  containerClass: string;
  iconClass: string;
  textClass: string;
  borderClass: string;
  icon: LucideIcon;
};
//#endregion

/**
 * 変換結果表示コンポーネント
 * @description 図面変換の結果を表示するコンポーネント
 */
export const ConversionResult: React.FC<ConversionResultProps> = memo(({ result }) => {
  //#region スタイル義
  const getStatusStyles = (): ResultStyle => {
    const styles: Record<'success' | 'error', ResultStyle> = {
      success: {
        containerClass: "bg-green-50",
        iconClass: "text-green-500",
        textClass: "text-green-800",
        borderClass: "border-green-200",
        icon: CheckCircle,
      },
      error: {
        containerClass: "bg-red-50",
        iconClass: "text-red-500",
        textClass: "text-red-800",
        borderClass: "border-red-200",
        icon: XCircle,
      },
    };

    return result.success ? styles.success : styles.error;
  };
  //#endregion

  //#region ヘルパー関数
  const extractFileName = (filePath: string): string => {
    return filePath.split("/").pop()?.split("\\").pop() || "unknown.file";
  };

  const renderWarnings = () => {
    if (result.warnings && result.warnings.length > 0) {
      return (
        <div className="mt-4 p-3 bg-yellow-50 rounded-md border border-yellow-200">
          <h4 className="text-sm font-medium text-yellow-800 mb-2">警告</h4>
          <ul className="list-disc list-inside text-sm text-yellow-700">
            {result.warnings.map((warning, index) => (
              <li key={index}>{warning}</li>
            ))}
          </ul>
        </div>
      );
    }
    return null;
  };

  const renderAdditionalInfo = () => {
    if (result.additionalInfo) {
      return (
        <div className="mt-4 p-3 bg-blue-50 rounded-md border border-blue-200">
          <h4 className="text-sm font-medium text-blue-800 mb-2">追加情報</h4>
          <ul className="list-disc list-inside text-sm text-blue-700">
            {Object.entries(result.additionalInfo).map(([key, value]) => (
              <li key={key}>
                <strong>{key}:</strong> {value}
              </li>
            ))}
          </ul>
        </div>
      );
    }
    return null;
  };
  //#endregion

  //#region UIレンダリング
  const styles = getStatusStyles();
  const StatusIcon = styles.icon;

  return (
    <Card shadow="sm" className={`border ${styles.borderClass} ${styles.containerClass}`}>
      <div className="p-4">
        <div className="flex items-start space-x-4">
          <div className={`flex-shrink-0 ${styles.iconClass}`}>
            <StatusIcon className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <h3 className={`text-lg font-medium ${styles.textClass}`}>
              {result.success ? "変換成功" : "変換失敗"}
            </h3>
            <p className={`mt-1 text-sm ${styles.textClass}`}>
              {result.message}
            </p>
          </div>
        </div>

        {result.success && result.convertedFilePath && (
          <div className="mt-4 text-sm">
            <div className={`p-3 rounded-md ${styles.containerClass} border ${styles.borderClass}`}>
              <div className="flex items-center space-x-2">
                <FileDown className={`h-4 w-4 ${styles.iconClass}`} />
                <span className="font-medium">変換後のファイル:</span>
                <span className="font-mono">
                  {extractFileName(result.convertedFilePath)}
                </span>
              </div>
            </div>
          </div>
        )}

        {!result.success && result.errorDetails && (
          <div className="mt-4 p-3 bg-red-50 rounded-md border border-red-200">
            <h4 className="text-sm font-medium text-red-800 mb-2">エラー詳細</h4>
            <p className="text-sm text-red-700">{result.errorDetails}</p>
          </div>
        )}

        {renderWarnings()}
        {renderAdditionalInfo()}

        {result.success && (
          <div className="mt-4 text-xs text-gray-500">
            ※ 変換されたファイルは一定時間後に自動的に削除されます。
            必要なファイルは必ずダウンロードしてください。
          </div>
        )}
      </div>
    </Card>
  );
  //#endregion
});

// DisplayNameの設定
ConversionResult.displayName = "ConversionResult";

export default ConversionResult;